import { type TransportHandler } from "@quatico/magellan-shared";

/**
 * Magellan wiring shared by client and server.
 *
 * Namespace and path BOTH carry the module name `trexpert-portal` — the name this module registers
 * with the platform and publishes in its `cds.mfe` manifest. That is not cosmetic: under app-base the
 * platform derives the browser's namespace→endpoint map from the module name, as
 * `<module>-<scope>` → `/cds/<module>/api/<scope>`. A module that answers to a different name is
 * simply absent from that map, and Magellan then falls back to a transport with no endpoint — so the
 * call fails before it reaches the network, with no request to see and no error to read. That is
 * exactly how `trexpert-auth` + `/trexpert/api` presented: a healthy pod, a correct token, and a UI
 * that only said the list could not be loaded.
 *
 * iam and ama already follow the convention. Keep these two in step with the manifest name.
 */
export const TREXPERT_NO_AUTH_NAMESPACE = "trexpert-portal-no-auth";
export const TREXPERT_NO_AUTH_ENDPOINT = "/cds/trexpert-portal/api/no-auth";
export const TREXPERT_TRANSPORT_NAME = "trexpert-fetch";

/**
 * Authenticated namespace (Phase 1). Mirrors the cpq-cds `…-auth` / `…-no-auth`
 * split: requests here pass through the IdentityContextResolver +
 * AuthorizationGuard. In dev the identity is carried by a persona header
 * (decision D2); on staging a real IAM-CDS bearer token replaces it.
 */
export const TREXPERT_AUTH_NAMESPACE = "trexpert-portal-auth";
export const TREXPERT_AUTH_ENDPOINT = "/cds/trexpert-portal/api/auth";

/** Dev-mode identity header: the signed-in persona's PersonId. */
export const TREXPERT_PERSON_HEADER = "x-trexpert-person";

/**
 * Fetch-based Magellan transport. Sends the JSON body the server's
 * `createFunctionRoute` expects: `{ name, data, namespace }`, where `data`
 * is the already-serialized payload string. In dev the client is served on
 * :3001 and proxies `/trexpert` to the API on :3000, so a relative endpoint
 * is same-origin.
 */
export const trexpertFetchFn: TransportHandler = async func => {
    const { name, payload, namespace, endpoint } = func;
    if (!name) {
        throw new Error('Cannot invoke a remote function without a "name".');
    }
    const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, data: payload, namespace }),
    });
    if (response.status >= 300) {
        return Promise.reject(
            new Error(`Remote function "${name}" failed: ${response.status} ${response.statusText}`)
        );
    }
    return response.text();
};
